'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  INITIAL_TOOLBAR_SCROLL_STATE,
  reduceToolbarVisibility,
  type ToolbarScrollState,
} from '../utils/reduce-toolbar-visibility.util';

export function useReaderToolbarVisibility({
  containerRef,
  pinned,
  resetKey,
}: {
  containerRef: React.RefObject<HTMLElement | null>;
  pinned: boolean;
  resetKey: string;
}) {
  const [visible, setVisible] = useState(true);
  const stateRef = useRef<ToolbarScrollState>(INITIAL_TOOLBAR_SCROLL_STATE);
  const lastScrollTopRef = useRef(0);
  const pinnedRef = useRef(pinned);
  const ignoreUntilRef = useRef(0);

  useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);

  useEffect(() => {
    const scrollElement = containerRef.current;
    if (!scrollElement) return;

    stateRef.current = INITIAL_TOOLBAR_SCROLL_STATE;
    lastScrollTopRef.current = scrollElement.scrollTop;
    ignoreUntilRef.current = Date.now() + 450;
    setVisible(true);

    let frame = 0;
    const syncRestoredPosition = () => {
      lastScrollTopRef.current = scrollElement.scrollTop;
    };
    const syncTimers = [0, 80, 200, 400].map((delay) =>
      window.setTimeout(syncRestoredPosition, delay),
    );

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const scrollTop = scrollElement.scrollTop;
        if (Date.now() < ignoreUntilRef.current) {
          lastScrollTopRef.current = scrollTop;
          return;
        }
        const delta = scrollTop - lastScrollTopRef.current;
        lastScrollTopRef.current = scrollTop;
        const next = reduceToolbarVisibility(stateRef.current, {
          scrollTop,
          delta,
          pinned: pinnedRef.current,
        });
        stateRef.current = next;
        setVisible((current) => (current === next.visible ? current : next.visible));
      });
    };

    scrollElement.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      syncTimers.forEach((timer) => window.clearTimeout(timer));
      cancelAnimationFrame(frame);
      scrollElement.removeEventListener('scroll', onScroll);
    };
  }, [containerRef, resetKey]);

  const toggle = useCallback(() => {
    ignoreUntilRef.current = Date.now() + 800;
    stateRef.current = {
      visible: !stateRef.current.visible,
      pendingDown: 0,
      pendingUp: 0,
    };
    setVisible(stateRef.current.visible);
  }, []);

  return { visible, toggle };
}
